/**
 * Centralized error logging utility
 * Week 1, Day 5 - Integration Testing & Bug Fixes
 * Week 2, Day 1 - Sentry Integration
 */

import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, any>;
  userId?: string;
  url?: string;
  userAgent?: string;
}

class ErrorLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  log(level: LogLevel, message: string, context?: Record<string, any>) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      userId: this.getUserId(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.logs.push(entry);

    // Keep only last N logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output (development)
    if (process.env.NODE_ENV === 'development') {
      const emoji = {
        debug: '🔍',
        info: 'ℹ️',
        warn: '⚠️',
        error: '❌',
        critical: '🚨',
      }[level];

      console[level === 'critical' || level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](
        `${emoji} [${level.toUpperCase()}] ${message}`,
        context
      );
    }

    // Send to Sentry in production for errors and critical issues
    if (process.env.NODE_ENV === 'production' && (level === 'error' || level === 'critical')) {
      this.sendToSentry(level, message, context);
    }
  }

  debug(message: string, context?: Record<string, any>) {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, any>) {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, any>) {
    this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, any>) {
    this.log('error', message, context);
  }

  critical(message: string, context?: Record<string, any>) {
    this.log('critical', message, context);
  }

  /**
   * Log API errors with consistent format
   */
  logApiError(endpoint: string, error: any, context?: Record<string, any>) {
    this.error(`API Error: ${endpoint}`, {
      ...context,
      endpoint,
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      response: error.response,
    });
  }

  /**
   * Log edge function errors
   */
  logEdgeFunctionError(functionName: string, error: any, context?: Record<string, any>) {
    this.error(`Edge Function Error: ${functionName}`, {
      ...context,
      functionName,
      error: error.message,
      details: error,
    });
  }

  /**
   * Log satellite processing errors
   */
  logSatelliteError(parcelId: string, analysisType: string, error: any) {
    this.error('Satellite Processing Error', {
      parcelId,
      analysisType,
      error: error.message,
      stack: error.stack,
    });
  }

  /**
   * Get recent logs for debugging
   */
  getRecentLogs(count = 20): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  private getUserId(): string | undefined {
    // Get from session storage or auth context
    try {
      const userId = sessionStorage.getItem('userId');
      return userId || undefined;
    } catch {
      return undefined;
    }
  }

  private sendToSentry(level: LogLevel, message: string, context?: Record<string, any>) {
    try {
      const userId = this.getUserId();
      
      // Set user context if available
      if (userId) {
        Sentry.setUser({ id: userId });
      }
      
      // Set additional context
      if (context) {
        Sentry.setContext('additional_data', context);
      }
      
      // Send to Sentry based on level
      if (level === 'critical') {
        Sentry.captureException(new Error(message), {
          level: 'fatal',
          tags: { severity: 'critical' },
        });
      } else {
        Sentry.captureException(new Error(message), {
          level: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to send error to Sentry:', error);
    }
  }
}

// Singleton instance
export const logger = new ErrorLogger();

// Convenience exports
export const logDebug = (message: string, context?: Record<string, any>) => logger.debug(message, context);
export const logInfo = (message: string, context?: Record<string, any>) => logger.info(message, context);
export const logWarn = (message: string, context?: Record<string, any>) => logger.warn(message, context);
export const logError = (message: string, context?: Record<string, any>) => logger.error(message, context);
export const logCritical = (message: string, context?: Record<string, any>) => logger.critical(message, context);
